import { collection, deleteDoc, doc, getDoc, getDocs, query, runTransaction, setDoc, Timestamp, updateDoc, where, } from "firebase/firestore";
import { prepareForFirestore } from "./utils";
export const subscriptionConverter = {
    toFirestore(value) {
        const subscription = value;
        return {
            ...prepareForFirestore(subscription),
            ...("nextBillingDate" in subscription && {
                nextBillingDate: toTimestampOrNull(subscription.nextBillingDate),
            }),
            ...("cancellationRequestedAt" in subscription && {
                cancellationRequestedAt: toTimestampOrNull(subscription.cancellationRequestedAt),
            }),
            ...("canceledAt" in subscription && {
                canceledAt: toTimestampOrNull(subscription.canceledAt),
            }),
        };
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        return {
            ...data,
            id: snapshot.id,
            nextBillingDate: data.nextBillingDate instanceof Timestamp
                ? data.nextBillingDate.toDate().toISOString()
                : data.nextBillingDate || null,
            cancellationRequestedAt: data.cancellationRequestedAt instanceof Timestamp
                ? data.cancellationRequestedAt.toDate().toISOString()
                : data.cancellationRequestedAt || null,
            canceledAt: data.canceledAt instanceof Timestamp
                ? data.canceledAt.toDate().toISOString()
                : data.canceledAt || null,
            createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate().toISOString()
                : data.createdAt || null,
            updatedAt: data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate().toISOString()
                : data.updatedAt || null,
        };
    },
};
const billingRecordConverter = {
    toFirestore(value) {
        const billingRecord = value;
        return {
            ...prepareForFirestore(billingRecord),
            ...("periodStart" in billingRecord && {
                periodStart: toTimestampOrNull(billingRecord.periodStart),
            }),
            ...("periodEnd" in billingRecord && {
                periodEnd: toTimestampOrNull(billingRecord.periodEnd),
            }),
            ...("payment" in billingRecord && {
                payment: billingRecord.payment
                    ? {
                        ...billingRecord.payment,
                        paidAt: toTimestampOrNull(billingRecord.payment.paidAt),
                    }
                    : null,
            }),
        };
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        return {
            ...data,
            id: snapshot.id,
            periodStart: data.periodStart instanceof Timestamp
                ? data.periodStart.toDate().toISOString()
                : data.periodStart || null,
            periodEnd: data.periodEnd instanceof Timestamp
                ? data.periodEnd.toDate().toISOString()
                : data.periodEnd || null,
            payment: data.payment
                ? {
                    ...data.payment,
                    paidAt: data.payment.paidAt instanceof Timestamp
                        ? data.payment.paidAt.toDate().toISOString()
                        : data.payment.paidAt || null,
                }
                : null,
            createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate().toISOString()
                : data.createdAt || null,
            updatedAt: data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate().toISOString()
                : data.updatedAt || null,
        };
    },
};
function toTimestampOrNull(value) {
    if (!value)
        return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        throw new Error("Invalid date value.");
    }
    return Timestamp.fromDate(date);
}
export function subscriptionsCollection(db) {
    return collection(db, "subscriptions").withConverter(subscriptionConverter);
}
export function subscriptionDocument(db, id) {
    return doc(db, "subscriptions", id).withConverter(subscriptionConverter);
}
export function billingRecordsCollection(db, subscriptionId) {
    return collection(db, "subscriptions", subscriptionId, "billingRecords").withConverter(billingRecordConverter);
}
export function billingRecordDocument(db, subscriptionId, id) {
    return doc(db, "subscriptions", subscriptionId, "billingRecords", id).withConverter(billingRecordConverter);
}
export async function fetchSubscription(db, id) {
    const snapshot = await getDoc(subscriptionDocument(db, id));
    return snapshot.exists() ? snapshot.data() : null;
}
export async function createSubscription(db, data) {
    validateSubscription(data);
    const existing = await getDocs(query(subscriptionsCollection(db), where("companyId", "==", data.companyId)));
    if (data.status !== "canceled" &&
        existing.docs.some((snapshot) => snapshot.data().status !== "canceled")) {
        throw new Error("This company already has a non-canceled subscription.");
    }
    const docRef = doc(subscriptionsCollection(db));
    const lockRef = doc(db, "subscriptionActiveLocks", data.companyId);
    await runTransaction(db, async (transaction) => {
        const lock = await transaction.get(lockRef);
        if (data.status !== "canceled" && lock.exists()) {
            throw new Error("This company already has a non-canceled subscription.");
        }
        transaction.set(docRef, {
            ...data,
            id: docRef.id,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        });
        if (data.status !== "canceled") {
            transaction.set(lockRef, {
                subscriptionId: docRef.id,
                companyId: data.companyId,
                updatedAt: Timestamp.now(),
            });
        }
    });
    return docRef.id;
}
export async function updateSubscription(db, id, data) {
    validateSubscription(data);
    const docRef = subscriptionDocument(db, id);
    const current = await getDoc(docRef);
    if (!current.exists()) {
        throw new Error("Subscription not found.");
    }
    const resultingStatus = data.status ?? current.data().status;
    const companyId = data.companyId ?? current.data().companyId;
    if (resultingStatus !== "canceled") {
        const existing = await getDocs(query(subscriptionsCollection(db), where("companyId", "==", companyId)));
        if (existing.docs.some((snapshot) => snapshot.id !== id && snapshot.data().status !== "canceled")) {
            throw new Error("This company already has a non-canceled subscription.");
        }
    }
    const lockRef = doc(db, "subscriptionActiveLocks", companyId);
    await runTransaction(db, async (transaction) => {
        const lock = await transaction.get(lockRef);
        const lockedSubscriptionId = lock.data()?.subscriptionId;
        if (resultingStatus !== "canceled" &&
            lock.exists() &&
            lockedSubscriptionId !== id) {
            throw new Error("This company already has a non-canceled subscription.");
        }
        transaction.update(docRef, {
            ...data,
            updatedAt: new Date().toISOString(),
        });
        if (resultingStatus === "canceled") {
            if (lockedSubscriptionId === id) {
                transaction.delete(lockRef);
            }
        }
        else {
            transaction.set(lockRef, {
                subscriptionId: id,
                companyId,
                updatedAt: Timestamp.now(),
            });
        }
    });
}
export async function deleteSubscription(db, id) {
    await deleteDoc(subscriptionDocument(db, id));
}
export async function fetchBillingRecord(db, subscriptionId, id) {
    const snapshot = await getDoc(billingRecordDocument(db, subscriptionId, id));
    return snapshot.exists() ? snapshot.data() : null;
}
export async function createBillingRecord(db, subscriptionId, data) {
    validateBillingRecord(data);
    const docRef = doc(billingRecordsCollection(db, subscriptionId));
    await setDoc(docRef, {
        ...data,
        id: docRef.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    });
    return docRef.id;
}
export async function updateBillingRecord(db, subscriptionId, id, data) {
    validateBillingRecord(data);
    const docRef = billingRecordDocument(db, subscriptionId, id);
    await updateDoc(docRef, {
        ...data,
        updatedAt: new Date().toISOString(),
    });
}
export async function deleteBillingRecord(db, subscriptionId, id) {
    await deleteDoc(billingRecordDocument(db, subscriptionId, id));
}
export function isSupportedCurrency(value) {
    return value === "ILS" || value === "USD";
}
export function validateSubscription(data) {
    if (data.currency !== undefined && !isSupportedCurrency(data.currency)) {
        throw new Error("Currency must be ILS or USD.");
    }
    if (data.price !== undefined &&
        (!Number.isFinite(data.price) || data.price < 0)) {
        throw new Error("Subscription price must be a non-negative number.");
    }
}
export function validateBillingRecord(data) {
    if (data.currency !== undefined && !isSupportedCurrency(data.currency)) {
        throw new Error("Currency must be ILS or USD.");
    }
    for (const [field, value] of [
        ["amount", data.amount],
        ["taxAmount", data.taxAmount],
        ["totalAmount", data.totalAmount],
    ]) {
        if (value !== undefined && (!Number.isFinite(value) || value < 0)) {
            throw new Error(`${field} must be a non-negative number.`);
        }
    }
    if (data.amount !== undefined &&
        data.taxAmount !== undefined &&
        data.totalAmount !== undefined &&
        Math.abs(data.totalAmount - (data.amount + data.taxAmount)) > 0.000001) {
        throw new Error("Total amount must equal amount plus tax.");
    }
    if (data.periodStart &&
        data.periodEnd &&
        new Date(data.periodEnd).getTime() < new Date(data.periodStart).getTime()) {
        throw new Error("Billing period end must not precede its start.");
    }
    if (data.status === "paid" && !data.payment?.paidAt) {
        throw new Error("Paid billing records require payment details.");
    }
}
